import { useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import LeadForm from "../components/LeadForm";
import PageMeta from "../components/PageMeta";
import { Reveal } from "../components/Reveal";
import { waLink, trackEvent, WA_NUMBER } from "../lib/api";

const Contact = () => {
  const [params] = useSearchParams();
  const intent = params.get("intent");
  const variant = intent === "quote" ? "quote" : intent === "service" ? "service" : "contact";

  return (
    <main data-testid="contact-page">
      <PageMeta title="Contact AccuSurge Medical Solutions | Request a Quote or Consultation" description="Contact AccuSurge for hospital setup, medical equipment, dialysis centres, financing support, licensing support and after-sales service. Submit your enquiry online." />
      <section className="bg-brand-950 pb-24 pt-40 md:pb-32 grain relative overflow-hidden">
        <div className="absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-accent/15 blur-[120px]" aria-hidden="true" />
        <div className="container-x relative">
          <Reveal>
            <span className="eyebrow text-accent">Contact Us</span>
            <h1 className="mt-6 max-w-4xl font-heading text-4xl md:text-6xl font-semibold tracking-tighter text-white text-balance" data-testid="contact-headline">
              Let's Build Better Healthcare Together.
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70">
              Tell us what you are planning — a new facility, an upgrade, equipment requirements or service support.
              Our team will respond with the right next step.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 -mt-14 pb-24">
        <div className="container-x grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_24px_70px_rgba(11,46,56,0.08)]" data-testid="contact-info-panel">
              <h2 className="font-heading text-xl font-semibold text-ink">Reach us directly</h2>
              <ul className="mt-8 space-y-6">
                <li className="flex items-start gap-4" data-testid="contact-address">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><MapPin className="h-5 w-5" /></span>
                  <div>
                    <p className="text-sm font-semibold text-ink">Our Offices</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-500">New Delhi (Karol Bagh) · Siliguri · Kolaghat · Kharagpur</p>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-phone">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><Phone className="h-5 w-5" /></span>
                  <div>
                    <p className="text-sm font-semibold text-ink">Phone</p>
                    <a href="tel:+919007337749" className="mt-1 block text-sm text-slate-500 transition-colors hover:text-brand-700">+91 90073 37749</a>
                    <p className="text-xs text-slate-400">Sagar Chakrabarti</p>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-email">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><Mail className="h-5 w-5" /></span>
                  <div>
                    <p className="text-sm font-semibold text-ink">Email</p>
                    <a href="mailto:sagarc@accusurge.co.in" className="mt-1 block text-sm text-slate-500 transition-colors hover:text-brand-700">sagarc@accusurge.co.in</a>
                  </div>
                </li>
              </ul>
              <div className="mt-8 rounded-xl bg-brand-900 p-6" data-testid="contact-whatsapp-card">
                <p className="font-heading text-base font-semibold text-white">Prefer WhatsApp?</p>
                <p className="mt-2 text-sm text-white/70">Message us directly — our team responds during business hours.</p>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { source: "contact_page" })}
                  className="btn-accent mt-5 w-full"
                  data-testid="contact-whatsapp-button"
                >
                  Chat on WhatsApp
                </a>
              </div>
              <p className="mt-6 text-xs leading-relaxed text-slate-400">
                Business hours: [To be updated] · Serving healthcare institutions across India.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-8 md:p-10" data-testid="contact-form-panel">
              <span className="eyebrow">
                {variant === "quote" ? "Request a Quote" : variant === "service" ? "Service Request" : "Send an Enquiry"}
              </span>
              <h2 className="mt-4 font-heading text-2xl font-semibold text-ink">
                {variant === "quote" ? "Share your product requirement" : variant === "service" ? "Tell us what needs service" : "Tell us about your requirement"}
              </h2>
              <div className="mt-8">
                <LeadForm key={variant} variant={variant} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Contact;
